import { useCallback, useEffect, useEffectEvent, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";
import LoadingIndicator from "ds/components/LoadingIndicator";
import useErrorHandle from "hooks/useErrorHandle";
import useChatConversation from "shared/ChatConversation/useChatConversation";
import useChatConversationCreate from "shared/ChatConversation/useChatConversationCreate";
import useChatConversationDelete from "shared/ChatConversation/useChatConversationDelete";
import useSearchChatConversations from "shared/ChatConversation/useSearchChatConversations";
import useSearchAiIntegrations from "shared/AiIntegration/useSearchAiIntegrations";
import useIntelligenceSettings from "shared/Intelligence/useIntelligenceSettings";
import { ArrowCircle, ErrorCircleColored, LogoSpaceliftColored } from "components/icons/generated";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import EmptyState from "ds/components/EmptyState";
// Imported via path-mapped specifier (not relative) so Storybook can swap it
// for a mock through the resolve.alias in .storybook/main.ts.
import useIntentMcpClient from "components/AssistantPanel/Chat/hooks/useIntentMcpClient";

import useAssistantPanelStore from "../Layout/useStore";
import useIsAssistantPageMode from "../hooks/useIsAssistantPageMode";
import ChatBudgetExhaustedBanner from "./BudgetExhaustedBanner";
import ChatBudgetProgress from "./BudgetProgress";
import AssistantPanelChatContent from "./Content";
import AssistantPanelHistorySidePanel from "./History/SidePanel";
import ChatApprovalModeSelect from "./ApprovalModeSelect";
import ChatIntegrationSelect from "./IntegrationSelect";
import useChatFrontendTools from "./tools";
import useChatStore from "./store/useStore";
import ChatModeSwitcher from "./ModeSwitcher";
import AssistantPanelChatSimpleHeader from "./SimpleHeader";
import { createChat, isChatStatusBusy } from "./helpers";

const AssistantPanelChat = () => {
  const {
    canUseInfraAssistant,
    loading: intelligenceSettingsLoading,
    error: intelligenceSettingsError,
  } = useIntelligenceSettings({ refreshOnMount: true });
  const { toolIcons: frontendToolIcons } = useChatFrontendTools();
  const { toolIcons: mcpToolIcons, isLoading: mcpLoading, error: mcpError } = useIntentMcpClient();

  const {
    integrations,
    initialLoading: integrationLoading,
    error: integrationError,
  } = useSearchAiIntegrations();

  const selectableIntegrations = useMemo(
    () => integrations.filter((i) => i.enabled && i.modelDetails.length > 0),
    [integrations]
  );

  const defaultIntegration = useMemo(
    () => selectableIntegrations.find((i) => i.isSpaceliftProvided) ?? selectableIntegrations[0],
    [selectableIntegrations]
  );

  const [createConversation] = useChatConversationCreate();
  const { isPanelOpen, setPanelOpen } = useAssistantPanelStore(
    useShallow((state) => ({ isPanelOpen: state.isOpen, setPanelOpen: state.setIsOpen }))
  );
  const isAssistantPageMode = useIsAssistantPageMode();

  const {
    conversations,
    loading: conversationsLoading,
    refetch: refetchConversations,
  } = useSearchChatConversations({ shouldPoll: () => isPanelOpen || isAssistantPageMode });
  const { loadConversation, loading: conversationLoading } = useChatConversation();
  const [deleteConversation] = useChatConversationDelete();

  const IntegrationErrorContent = useErrorHandle(integrationError);

  const {
    startChat,
    openChat,
    resetChat,
    setConversationError,
    conversationError,
    chatId,
    setIntegrationId,
    setModel,
    integrationId,
    model,
  } = useChatStore(
    useShallow((state) => ({
      startChat: state.startChat,
      openChat: state.openChat,
      resetChat: state.resetChat,
      setConversationError: state.setConversationError,
      conversationError: state.conversationError,
      chatId: state.chat?.id,
      setIntegrationId: state.setIntegrationId,
      setModel: state.setModel,
      integrationId: state.integrationId,
      model: state.model,
    }))
  );

  const toolIcons = useMemo(
    () => ({ ...frontendToolIcons, ...mcpToolIcons }),
    [frontendToolIcons, mcpToolIcons]
  );

  const handleFirstMessage = useCallback(
    async (message: string) => {
      const result = await createConversation({});
      const id = result?.data?.intentChatConversationCreate.id;

      if (id) {
        startChat(createChat(id), message);
        refetchConversations();
      } else {
        setConversationError("Failed to create conversation");
      }
    },
    [createConversation, refetchConversations, startChat, setConversationError]
  );

  const handleNewChat = useCallback(() => {
    resetChat();
  }, [resetChat]);

  const handleDeleteChat = useCallback(
    async (deletedChatId: string) => {
      await deleteConversation({ id: deletedChatId });
      refetchConversations();

      if (deletedChatId === useChatStore.getState().chat?.id) {
        resetChat();
      }
    },
    [deleteConversation, refetchConversations, resetChat]
  );

  const handleSelectChat = useCallback(
    async (selectedChatId: string) => {
      if (selectedChatId === chatId) return;

      resetChat();

      const result = await loadConversation(selectedChatId);
      if (result) {
        openChat({
          chat: createChat(selectedChatId, result.messages),
          version: result.version,
          integrationId: result.integrationId ?? defaultIntegration?.id,
          model: result.model ?? defaultIntegration?.modelDetails[0]?.id,
        });
      } else {
        setConversationError("Failed to load conversation");
      }
    },
    [
      chatId,
      loadConversation,
      resetChat,
      openChat,
      setConversationError,
      defaultIntegration?.id,
      defaultIntegration?.modelDetails,
    ]
  );

  const canShowBudgetProgress = useMemo(
    () => integrations.find((i) => i.id === integrationId)?.tokenUsage?.totalTokens !== -1,
    [integrations, integrationId]
  );

  const handleReloadChat = useCallback(async () => {
    const chat = useChatStore.getState().chat;
    if (!chat) return;

    const result = await loadConversation(chat.id);
    const isStale = useChatStore.getState().chat !== chat || isChatStatusBusy(chat.status);
    if (!result || isStale) return;

    // Same instance on purpose: replacing it would drop the error the composer shows.
    chat.messages = result.messages;
    openChat({
      chat,
      version: result.version,
      integrationId: result.integrationId,
      model: result.model,
    });
  }, [loadConversation, openChat]);

  const setDefaultIntegrationAndModel = useEffectEvent(() => {
    const defaultModelId = defaultIntegration?.modelDetails[0]?.id;

    if (!defaultIntegration?.id || !defaultModelId) {
      return;
    }

    const { integrationId: currentId, model: currentModel } = useChatStore.getState();
    if (currentId === defaultIntegration.id && currentModel === defaultModelId) {
      return;
    }

    setIntegrationId(defaultIntegration.id);
    setModel(defaultModelId);
  });

  useEffect(() => {
    if (integrationLoading) {
      return;
    }

    const isValid =
      integrationId &&
      model &&
      integrations.some((i) =>
        i.id === integrationId ? i.enabled && i.modelDetails.some((m) => m.id === model) : false
      );

    if (!isValid) {
      setDefaultIntegrationAndModel();
    }
  }, [integrationId, integrationLoading, integrations, model]);

  const showTermsOverlay =
    !intelligenceSettingsLoading && !intelligenceSettingsError && !canUseInfraAssistant;

  if (mcpLoading || integrationLoading) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <LoadingIndicator loading={true} />
        </Box>
      </Box>
    );
  }

  if (mcpError) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <EmptyState
            icon={ErrorCircleColored}
            title="An error occurred, please try refreshing the app."
            caption="If the error persists, contact support."
          >
            <Button
              variant="secondary"
              startIcon={ArrowCircle}
              size="small"
              onPress={() => window.location.reload()}
            >
              Refresh page
            </Button>
          </EmptyState>
        </Box>
      </Box>
    );
  }

  if (IntegrationErrorContent) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          {IntegrationErrorContent}
        </Box>
      </Box>
    );
  }

  if (!defaultIntegration && !showTermsOverlay) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <EmptyState icon={LogoSpaceliftColored} title="No AI integration available">
            <LinkButton
              to="/integrations/ai"
              variant="secondary"
              size="small"
              onClick={() => {
                if (!isAssistantPageMode) {
                  setPanelOpen(false);
                }
              }}
            >
              Set up an AI integration
            </LinkButton>
          </EmptyState>
        </Box>
      </Box>
    );
  }

  if (conversationError) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          {conversationError}
        </Box>
      </Box>
    );
  }

  return (
    <Box direction="row" fullHeight>
      {!showTermsOverlay && (
        <AssistantPanelHistorySidePanel
          deleteChat={handleDeleteChat}
          chats={conversations}
          loading={conversationsLoading}
          onSelectChat={handleSelectChat}
        />
      )}
      <Box direction="column" fullHeight relative grow="1" zeroMinWidth>
        <AssistantPanelChatContent
          toolIcons={toolIcons}
          onFirstMessage={handleFirstMessage}
          onNewChat={handleNewChat}
          conversations={conversations}
          conversationsLoading={conversationsLoading}
          conversationLoading={conversationLoading}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onRefetchConversations={refetchConversations}
          onReloadChat={handleReloadChat}
          beforeComposer={<ChatBudgetExhaustedBanner />}
          showTermsOverlay={showTermsOverlay}
          composerToolbarSlot={
            <Box align="center" shrink="0">
              <ChatModeSwitcher />
            </Box>
          }
          composerFooterSlot={
            <Box align="center" padding="0 large" gap="medium" justify="between" fullWidth>
              <Box align="center" shrink="0">
                <ChatApprovalModeSelect />
              </Box>
              <Box zeroMinWidth gap="medium" align="center">
                <ChatIntegrationSelect />
                {canShowBudgetProgress && <ChatBudgetProgress />}
              </Box>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

export default AssistantPanelChat;
